import postRobot, { Cancellable } from 'post-robot';
import invariant from 'invariant';

import {
  FetchHandler,
  GetHandler,
  PostHandler,
  Proxy,
  RequestOptions,
  SerializedRequestOptions,
  SerializedResponse,
} from './types';
import { serializeRequestOptions, serializeResponse } from './serializers';
import { deserializeRequestOptions, deserializeResponse } from './deserializers';
import { verifyRequestOptions } from './utils';
import { MICROSCOPE_ENDPOINT, STATLAS_ENDPOINT } from './config';

export const buildProxy = (opts?: { domain?: string; window?: Window; name?: string }): Proxy => {
  const fetchProxy: FetchHandler = async (path, options) => {
    invariant(path, 'proxy.fetch was called without an path as the first parameter.');

    invariant(
      !path.startsWith('http'),
      `proxy.fetch method expects a path to the service. But it was called with a url.`,
    );

    verifyRequestOptions(options);

    return postRobot
      .send(
        opts?.window ?? window.parent,
        opts?.name ?? 'proxy.fetch',
        {
          path,
          ...(options ? { options: await serializeRequestOptions(options) } : {}),
        },
        opts?.domain ? { domain: opts.domain } : undefined,
      )
      .then((event) => {
        const response = event.data as SerializedResponse;
        return deserializeResponse(response);
      });
  };

  const callFetch = <T extends any>(
    path: string,
    method: string,
    data?: T,
    options?: Omit<RequestOptions, 'method' | 'body'>,
  ) => {
    const headers = new Headers(options?.headers);
    headers.set('Content-Type', 'application/json');

    const init = { method, ...(data ? { body: JSON.stringify(data) } : {}), headers };
    const opts = { ...options, ...init };
    return fetchProxy(path, opts).then(async (r: Response) => {
      let data = await r.text();
      try {
        data = JSON.parse(data);
      } catch (e) {}

      return {
        status: r.status,
        statusText: r.statusText,
        data: data as any,
        headers: r.headers,
      };
    });
  };

  const getProxy: GetHandler = (path, options) => callFetch(path, 'get', undefined, options);

  const delProxy: GetHandler = (path, options) => callFetch(path, 'delete', undefined, options);

  const postProxy: PostHandler = (path, data, options) => callFetch(path, 'post', data, options);

  const putProxy: PostHandler = (path, data, options) => callFetch(path, 'put', data, options);

  const patchProxy: PostHandler = (path, data, options) => callFetch(path, 'patch', data, options);

  return {
    fetch: fetchProxy,
    get: getProxy,
    delete: delProxy,
    post: postProxy,
    put: putProxy,
    patch: patchProxy,
  };
};

export const setupProxyTrap = (handler: FetchHandler, opts?: { domain?: string; name?: string }): Cancellable => {
  const options = {
    ...(opts?.domain ? { domain: opts?.domain } : {}),
  };

  return postRobot.on(opts?.name ?? 'proxy.fetch', options, (e) => {
    const { path, options } = e.data as { path: string; options?: SerializedRequestOptions };
    const f = () => (options ? handler(path, deserializeRequestOptions(options)) : handler(path));
    return f().then((r) => serializeResponse(r));
  });
};

export const proxyTrap = (handler: FetchHandler): Cancellable => setupProxyTrap(handler, { domain: STATLAS_ENDPOINT });

export const proxy = buildProxy({ domain: MICROSCOPE_ENDPOINT });
